import React, { useEffect, useState } from 'react';
import { Dropdown, IDropdownOption, Label, Stack, IDropdownStyles, MessageBar, MessageBarType } from '@fluentui/react';
import { useLocalStorageContext } from '../../Provider/LocalStorageProvider';
import { dateFormatOptions } from '../../Utils/DateFormatUtility';

interface DateFormatDropdownProps {
  label?: string;
  styles?: React.CSSProperties;
  onChange?: (format: string) => void;
  initialValue?: string;
}

export const DateFormatDropdown: React.FC<DateFormatDropdownProps> = ({ 
  label = "Date Format", 
  styles = {},
  onChange,
  initialValue
}) => {
  const { dateFormat, setUser } = useLocalStorageContext();
  const [selectedKey, setSelectedKey] = useState<string>(initialValue || dateFormat || 'DD-MM-YYYY');
  const [error, setError] = useState<string | null>(null);
  
  // Priority 1: Backend value from initialValue
  useEffect(() => {
    if (initialValue) {
      console.log(`Setting dropdown to backend value: ${initialValue}`);
      
      // Validate that the initialValue is one of our supported formats
      const isValidFormat = dateFormatOptions.some(option => option.key === initialValue);
      
      if (isValidFormat) {
        setSelectedKey(initialValue);
        setUser(prevUser => ({
          ...prevUser,
          dateFormat: initialValue
        }));
      } else {
        console.warn(`Invalid date format received from backend: ${initialValue}`);
        setError(`Received unsupported date format: ${initialValue}. Using default format instead.`);
        
        // Use default format instead
  const defaultFormat = 'DD-MM-YYYY';
        setSelectedKey(defaultFormat);
        setUser(prevUser => ({
          ...prevUser,
          dateFormat: defaultFormat
        }));
        
        // Log error to console instead of using trackException
        console.error(`Invalid date format in DateFormatDropdown: ${initialValue}`);
      }
    }
  }, [initialValue, setUser]);

  // Priority 2: Context value if no backend value is available
  useEffect(() => {
    if (!initialValue && dateFormat) {
      console.log(`No backend value, using context value: ${dateFormat}`);
      
      // Validate that the context format is one of our supported formats
      const isValidFormat = dateFormatOptions.some(option => option.key === dateFormat);
      
      if (isValidFormat) {
        setSelectedKey(dateFormat);
      } else {
        console.warn(`Invalid date format in context: ${dateFormat}`);
        
        // Use default format instead
        const defaultFormat = 'DD/MM/YYYY';
        setSelectedKey(defaultFormat);
        setUser(prevUser => ({
          ...prevUser,
          dateFormat: defaultFormat
        }));
      }
    }
  }, [initialValue, dateFormat, setUser]);

  // Handler for dropdown selection change
  const onFormatChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      const newFormat = option.key as string;
      console.log(`User selected new date format: ${newFormat}`);
      
      // Clear any previous errors
      setError(null);
      
      setSelectedKey(newFormat);
      
      // Update user preferences in LocalStorageContext
      setUser(prevUser => ({
        ...prevUser,
        dateFormat: newFormat
      }));
      
      // Also save to localStorage directly for redundancy
      localStorage.setItem('dateFormat', newFormat);
      
      // Call the onChange callback if provided to update backend
      if (onChange) {
        onChange(newFormat);
      }
    }
  };

  // Create dropdown options with examples
  const options: IDropdownOption[] = dateFormatOptions.map(format => ({
    key: format.key,
    text: `${format.text} (e.g., ${format.example})`
  }));

  // Custom styles for dropdown
  const dropdownStyles: Partial<IDropdownStyles> = {
    dropdown: { width: 300 },
    title: { border: '1px solid #8a8886' }
  };

  return (
    <Stack>
      {error && (
        <MessageBar 
          messageBarType={MessageBarType.warning}
          isMultiline={false}
          dismissButtonAriaLabel="Close"
          onDismiss={() => setError(null)}
          styles={{ root: { marginBottom: 10 } }}
        >
          {error}
        </MessageBar>
      )}
      <Label>{label}</Label>
      <Dropdown
        selectedKey={selectedKey}
        options={options}
        onChange={onFormatChange}
        styles={dropdownStyles}
        ariaLabel="Date format selection"
        calloutProps={{ calloutMaxHeight: 300 }}
      />
    </Stack>
  );
};